/**
 * Intersection Observer Animation Controller (TC.IO)
 *
 * A lightweight, high-performance replacement for AOS (Animate On Scroll).
 * Uses native IntersectionObserver to toggle the `.io-animate` class.
 *
 * Supported HTML Data Attributes:
 *
 * 1. data-io="[animation-type]"
 *    Defines the animation direction/style.
 *    - Fade:            fade-up, fade-down, fade-right, fade-left, fade-up-right, fade-up-left, fade-down-right, fade-down-left
 *    - Zoom:            zoom-in, zoom-in-up, zoom-in-down, zoom-in-right, zoom-in-left, zoom-out, zoom-out-up, zoom-out-down, zoom-out-right, zoom-out-left
 *    - Slide:           slide-up, slide-down, slide-right, slide-left
 *    - Flip:            flip-left, flip-right, flip-up, flip-down
 *
 * 2. data-io-duration="[ms]"
 *    The duration of the animation transition in milliseconds.
 *    - Values:          50 to 3000 (increments of 50, e.g. "800")
 *    - Default:         400
 *
 * 3. data-io-delay="[ms]"
 *    The delay before starting the animation in milliseconds.
 *    - Values:          50 to 3000 (increments of 50, e.g. "200")
 *    - Default:         0
 *
 * 4. data-io-easing="[easing-type]"
 *    The transition timing function.
 *    - Values:          linear, ease, ease-in, ease-out, ease-in-out
 *                       ease-in-back, ease-out-back, ease-in-out-back
 *                       ease-in-sine, ease-out-sine, ease-in-out-sine
 *                       ease-in-quad, ease-out-quad, ease-in-out-quad
 *                       ease-in-cubic, ease-out-cubic, ease-in-out-cubic
 *                       ease-in-quart, ease-out-quart, ease-in-out-quart
 *    - Default:         ease
 *
 * 5. data-io-once="[boolean]"
 *    Whether the animation should run only once or repeat on scroll entry.
 *    - Values:          "true", "false"
 *    - Default:         "true"
 *
 * 6. data-io-offset="[pixels]"
 *    The distance (in px) from the bottom of the viewport before the animation triggers.
 *    - Default:         120
 *
 * @package TekCreative
 * @since 2.0.0
 */

window.TC = window.TC || {};

TC.IO = {
	observers: new Map(), // Cached observers by rootMargin
	defaultOffset: 120,

	/**
	 * Get or create an observer for a specific offset (rootMargin)
	 * @param {number} offset
	 * @returns {IntersectionObserver}
	 */
	getObserver(offset) {
		const rootMargin = `0px 0px -${offset}px 0px`;
		if (this.observers.has(rootMargin)) {
			return this.observers.get(rootMargin);
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const el = entry.target;
					const once = el.getAttribute('data-io-once') !== 'false';

					if (entry.isIntersecting) {
						el.classList.add('io-animate');
						if (once) {
							// Stop observing if once is true
							observer.unobserve(el);
						}
					} else {
						if (!once) {
							// Remove class on exit if once is false (allows repeat animations)
							el.classList.remove('io-animate');
						}
					}
				});
			},
			{
				root: null, // viewport
				rootMargin: rootMargin,
				threshold: 0,
			}
		);

		this.observers.set(rootMargin, observer);
		return observer;
	},

	/**
	 * Initialize animation observation for a single element
	 * @param {HTMLElement} el
	 */
	initElement(el) {
		if (el.dataset.ioObserved) {
			return;
		}

		const offsetAttr = el.getAttribute('data-io-offset');
		const offset = offsetAttr ? parseInt(offsetAttr, 10) : this.defaultOffset;
		
		const observer = this.getObserver(isNaN(offset) ? this.defaultOffset : offset);
		observer.observe(el);
		
		el.dataset.ioObserved = 'true';
	},

	/**
	 * Scan and initialize all elements with [data-io] inside a container or globally
	 * @param {HTMLElement|Document} container
	 */
	init(container = document) {
		const elements = container.querySelectorAll('[data-io]');
		elements.forEach((el) => this.initElement(el));
	},

	/**
	 * Refresh or re-scan elements (useful for dynamic content or ACF block previews)
	 * @param {HTMLElement|Document} container
	 */
	refresh(container = document) {
		this.init(container);
	},

	/**
	 * Disconnect all observers and clear cache
	 */
	destroy() {
		this.observers.forEach((observer) => observer.disconnect());
		this.observers.clear();
		
		// Remove observed flag from all elements
		const elements = document.querySelectorAll('[data-io]');
		elements.forEach((el) => {
			delete el.dataset.ioObserved;
			el.classList.remove('io-animate');
		});
	}
};

// Auto-initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
	TC.IO.init();
});

export default TC.IO;
